import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { hasAnyPermission, hasPermission } from '../../utils/permissions';
import styles from './SettingsPage.module.css';

const helperTables = [
  {
    key: 'sha-interventions',
    title: 'SHA Interventions',
    description: 'Maintain SHA intervention codes and descriptions for mapping to procedures and equipment.',
    status: 'ready',
    actionLabel: 'Manage SHA Interventions',
    actionTo: '/procedures',
    resource: 'procedures',
  },
  {
    key: 'modalities',
    title: 'Dropdown Config',
    description: 'Maintain categorized dropdown data (e.g., modality, facility_hmis) with code, name, and status.',
    status: 'ready',
    actionLabel: 'Manage Dropdown Config',
    actionTo: '/settings/modalities',
    resource: 'equipment_mapping',
  },
  {
    key: 'procedure-mapping',
    title: 'Equipment Mapping',
    description: 'Map Modality codes attached to equipment to map procedures to equipment based on modality.',
    status: 'ready',
    actionLabel: 'Open Equipment Mapping',
    actionTo: '/procedure-mapping',
    resource: 'equipment_mapping',
  },
  {
    key: 'revenue-distribution',
    title: 'Revenue Distribution',
    description: 'Manage active vendor and facility revenue percentages by effective date period.',
    status: 'ready',
    actionLabel: 'Manage Revenue Distribution',
    actionTo: '/settings/revenue-distribution',
    resource: 'revenue_distribution',
  },
  {
    key: 'error-logs',
    title: 'API Error Logs',
    description: 'Review failed API requests by resource and time period, including diagnostic details.',
    status: 'ready',
    actionLabel: 'View Error Logs',
    actionTo: '/settings/error-logs',
    resource: 'error_logs',
  },
  {
    key: 'dicom-errors',
    title: 'DICOM Errors',
    description: 'Review machines refused by Orthanc, failed pings, filtered-out worklists and MPPS problems.',
    status: 'ready',
    actionLabel: 'View DICOM Errors',
    actionTo: '/settings/dicom-errors',
    resource: 'error_logs',
  },
  {
    key: 'sha-verification-logs',
    title: 'SHA Verification Calls',
    description: 'Every call to the SHA verification API: verified, not verified or failed, with caller, IP and time.',
    status: 'ready',
    actionLabel: 'View SHA Verification Calls',
    actionTo: '/settings/sha-verification-logs',
    resource: 'error_logs',
  },
  {
    key: 'public-usage-access',
    title: 'Public Usage Access',
    description: 'Set the access code for the public equipment usage page that opens without a login.',
    status: 'ready',
    actionLabel: 'Manage Access Code',
    actionTo: '/settings/public-usage-access',
    resource: 'public_access',
  },
  {
    key: 'login-history',
    title: 'Login History',
    description: 'Review successful and failed logins by user, time, IP address, and client agent.',
    status: 'ready',
    actionLabel: 'View Login History',
    actionTo: '/settings/login-history',
    resource: 'login_history',
  },
  {
    key: 'users-permissions',
    title: 'Users & Permissions',
    description: 'Create application users, update account status, and assign permission access.',
    status: 'ready',
    actionLabel: 'Manage Users',
    actionTo: '/settings/users',
    anyPermissions: [
      { resource: 'users' },
      { resource: 'permissions' },
    ],
  },
//   {
//     key: 'counties',
//     title: 'Counties',
//     description: 'Structure reserved for county helper table maintenance in settings.',
//     status: 'planned',
//   },
];

const StatusBadge = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  return (
    <span className={`${styles.badge} ${normalized === 'ready' ? styles.ready : styles.planned}`}>
      {normalized === 'ready' ? 'Ready' : 'Planned'}
    </span>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const visibleTables = helperTables.filter((item) => (
    item.anyPermissions
      ? hasAnyPermission(user, item.anyPermissions)
      : hasPermission(user, item.resource)
  ));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Settings</h1>
        <p>Backend Helper Tables, configurations and mappings</p>
      </header>

      <section className={styles.panel}>

        <div className={styles.grid}>
          {visibleTables.map((item) => (
            <article className={styles.card} key={item.key}>
              <div className={styles.cardTop}>
                <h3>{item.title}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p>{item.description}</p>
              {item.actionTo ? (
                <Link className={styles.actionLink} to={item.actionTo}>
                  {item.actionLabel}
                </Link>
              ) : (
                <button className={styles.disabledAction} type="button" disabled>
                  Coming Soon
                </button>
              )}
            </article>
          ))}
          {visibleTables.length === 0 ? (
            <p>You do not have access to any settings areas.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default SettingsPage;
